import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle, Globe, Heart, Shield, TrendingDown, Plane, Users, Award, Mail, Instagram } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';

// Initialize EmailJS at module level
emailjs.init({ publicKey: 'hRDdIV09HRsYHvn6j' });

// Custom X (Twitter) Logo Component
const XIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BENEFIT_ICONS = [Globe, TrendingDown, Heart, Shield, TrendingDown, Plane];

const Home = () => {
  const { lang, switchLanguage, t, isRtl } = useLanguage();
  const [formData, setFormData] = useState({
    fullName: '', email: '', nationality: '', monthlyIncome: '',
    employmentType: '', gdprConsent: false, honeypot: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update page meta on language change
  useEffect(() => {
    document.title = t('meta.title');
    const setMeta = (sel, attr, val) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', t('meta.description'));
    setMeta('meta[property="og:title"]', 'content', t('meta.ogTitle'));
    setMeta('meta[property="og:description"]', 'content', t('meta.ogDescription'));
    setMeta('meta[name="twitter:title"]', 'content', t('meta.ogTitle'));
    setMeta('meta[name="twitter:description"]', 'content', t('meta.ogDescription'));
  }, [lang, t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, employmentType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[Form] 1. Submit started');

    if (isSubmitting) {
      console.log('[Form] Blocked: double submit');
      return;
    }

    if (!formData.fullName || !formData.email || !formData.nationality || !formData.monthlyIncome) {
      toast.error(t('contact.validation.missingFields'));
      console.log('[Form] Blocked: missing fields', formData);
      return;
    }
    if (!formData.employmentType) {
      toast.error(t('contact.validation.missingEmployment'));
      console.log('[Form] Blocked: no employment type selected');
      return;
    }
    console.log('[Form] 2. Field validation passed');

    if (!formData.gdprConsent) {
      toast.error(t('contact.validation.gdprRequired'));
      console.log('[Form] 3. Blocked: GDPR not checked');
      return;
    }
    console.log('[Form] 3. GDPR check passed');

    if (formData.honeypot) {
      console.log('[Form] 4. Blocked: honeypot filled');
      return;
    }
    console.log('[Form] 4. Honeypot check passed');

    setIsSubmitting(true);
    console.log('[Form] 5. All validation passed, calling EmailJS...');

    try {
      const params = {
        fullName: formData.fullName,
        email: formData.email,
        nationality: formData.nationality,
        monthlyIncome: formData.monthlyIncome,
        employmentType: formData.employmentType === 'business_owner' ? 'Business Owner' : 'Remote Employee',
      };
      console.log('[EmailJS] 6. Before send, params:', JSON.stringify(params));

      const result = await emailjs.send('service_xzuypr6', 'template_lr7gm4x', params);
      console.log('[EmailJS] 7. After send, status:', result.status, result.text);

      toast.success(t('contact.validation.success'));
      setFormData({
        fullName: '', email: '', nationality: '', monthlyIncome: '',
        employmentType: '', gdprConsent: false, honeypot: ''
      });

      try {
        await axios.post(`${BACKEND_URL}/api/submit-lead`, { ...formData, honeypot: '' });
        console.log('[Sheets] 8. Google Sheets logged');
      } catch (sheetErr) {
        console.warn('[Sheets] 8. Sheets failed (non-blocking):', sheetErr.message);
      }
    } catch (err) {
      console.error('[EmailJS] CAUGHT ERROR:', err);
      toast.error(t('contact.validation.error'));
    }

    setIsSubmitting(false);
    console.log('[Form] 9. Done');
  };

  const scrollToContact = () => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  const scrollToEligibility = () => document.getElementById('eligibility')?.scrollIntoView({ behavior: 'smooth' });

  const facts = t('whiteCard.facts');
  const benefitItems = t('benefits.items');
  const lifestyleItems = t('lifestyle.items');
  const pricingItems = t('pricing.items');
  const privacyItems = t('privacy.items');
  const eligRemoteItems = t('eligibility.remote.items');
  const eligBusinessItems = t('eligibility.business.items');
  const aboutStats = t('about.stats');

  return (
    <div className={`landing-page${isRtl ? ' rtl' : ''}`}>
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <div className="logo">{t('header.logo')}</div>
          <nav className="nav">
            <a href="#about" className="nav-link">{t('header.about')}</a>
            <a href="#benefits" className="nav-link">{t('header.benefits')}</a>
            <a href="#pricing" className="nav-link">{t('header.pricing')}</a>
            <div className="nav-social">
              <a href="https://www.instagram.com/agm_consulting_bp/" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Instagram">
                <Instagram size={20} />
              </a>
              <a href="https://x.com/AGM_BP" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="X (Twitter)">
                <XIcon size={20} />
              </a>
            </div>
            {/* Language Switcher */}
            <div className="lang-switcher" data-testid="lang-switcher">
              <button onClick={() => switchLanguage('en')} className={`lang-btn ${lang === 'en' ? 'lang-active' : ''}`} data-testid="lang-en" aria-label="Switch to English">EN</button>
              <span className="lang-divider">|</span>
              <button onClick={() => switchLanguage('ru')} className={`lang-btn ${lang === 'ru' ? 'lang-active' : ''}`} data-testid="lang-ru" aria-label="Switch to Russian">RU</button>
              <span className="lang-divider">|</span>
              <button onClick={() => switchLanguage('ar')} className={`lang-btn ${lang === 'ar' ? 'lang-active' : ''}`} data-testid="lang-ar" aria-label="Switch to Arabic">AR</button>
              <span className="lang-divider">|</span>
              <button onClick={() => switchLanguage('es')} className={`lang-btn ${lang === 'es' ? 'lang-active' : ''}`} data-testid="lang-es" aria-label="Switch to Spanish">ES</button>
            </div>
            <Button onClick={scrollToContact} className="cta-nav-btn">{t('header.contactUs')}</Button>
          </nav>
          {/* Mobile language switcher - visible when nav is hidden */}
          <div className="mobile-lang-switcher" data-testid="mobile-lang-switcher">
            <button onClick={() => switchLanguage('en')} className={`lang-btn ${lang === 'en' ? 'lang-active' : ''}`} aria-label="Switch to English">EN</button>
            <span className="lang-divider">|</span>
            <button onClick={() => switchLanguage('ru')} className={`lang-btn ${lang === 'ru' ? 'lang-active' : ''}`} aria-label="Switch to Russian">RU</button>
            <span className="lang-divider">|</span>
            <button onClick={() => switchLanguage('ar')} className={`lang-btn ${lang === 'ar' ? 'lang-active' : ''}`} aria-label="Switch to Arabic">AR</button>
            <span className="lang-divider">|</span>
            <button onClick={() => switchLanguage('es')} className={`lang-btn ${lang === 'es' ? 'lang-active' : ''}`} aria-label="Switch to Spanish">ES</button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">{t('hero.title')}</h1>
          <h2 className="hero-subtitle">{t('hero.subtitle')}</h2>
          <p className="hero-description">{t('hero.description')}</p>
          <div className="hero-cta">
            <Button onClick={scrollToEligibility} size="lg" className="btn-primary-hero">
              {t('hero.checkEligibility')}
            </Button>
            <Button onClick={scrollToContact} size="lg" variant="outline" className="btn-secondary-hero">
              {t('hero.bookConsultation')}
            </Button>
          </div>
        </div>
      </section>

      {/* What is Hungary's White Card */}
      <section className="section" id="white-card">
        <div className="container">
          <h2 className="section-title">{t('whiteCard.title')}</h2>
          <p className="section-description">{t('whiteCard.description')}</p>
          <div className="facts-grid">
            {Array.isArray(facts) && facts.map((fact, i) => (
              <Card className="fact-card" key={i}>
                <CardContent className="fact-content">
                  <CheckCircle className="fact-icon" />
                  <h3 className="fact-title">{fact.title}</h3>
                  <p className="fact-text">{fact.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits of Hungary */}
      <section className="section section-dark" id="benefits">
        <div className="container">
          <h2 className="section-title">{t('benefits.title')}</h2>
          <div className="benefits-grid">
            {Array.isArray(benefitItems) && benefitItems.map((item, i) => {
              const Icon = BENEFIT_ICONS[i] || Globe;
              return (
                <div className="benefit-item" key={i}>
                  <Icon className="benefit-icon" />
                  <h3 className="benefit-title">{item.title}</h3>
                  <p className="benefit-text">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="section" id="eligibility">
        <div className="container">
          <h2 className="section-title">{t('eligibility.title')}</h2>
          <div className="eligibility-grid">
            <Card className="eligibility-card">
              <CardContent className="eligibility-content">
                <Users className="eligibility-icon" />
                <h3 className="eligibility-title">{t('eligibility.remote.title')}</h3>
                <p className="eligibility-text">{t('eligibility.remote.description')}</p>
                <ul className="eligibility-list">
                  {Array.isArray(eligRemoteItems) && eligRemoteItems.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </CardContent>
            </Card>
            <Card className="eligibility-card">
              <CardContent className="eligibility-content">
                <Award className="eligibility-icon" />
                <h3 className="eligibility-title">{t('eligibility.business.title')}</h3>
                <p className="eligibility-text">{t('eligibility.business.description')}</p>
                <ul className="eligibility-list">
                  {Array.isArray(eligBusinessItems) && eligBusinessItems.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Digital Nomads Choose Hungary */}
      <section className="section lifestyle-section">
        <div className="container">
          <div className="lifestyle-content">
            <div className="lifestyle-text">
              <h2 className="section-title">{t('lifestyle.title')}</h2>
              <p className="lifestyle-description">{t('lifestyle.description')}</p>
              <ul className="lifestyle-list">
                {Array.isArray(lifestyleItems) && lifestyleItems.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
            <div className="lifestyle-image">
              <img src="https://images.pexels.com/photos/1212818/pexels-photo-1212818.jpeg" alt={t('lifestyle.imageAlt')} />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="section section-dark" id="pricing">
        <div className="container">
          <h2 className="section-title">{t('pricing.title')}</h2>
          <Card className="pricing-card">
            <CardContent className="pricing-content">
              <div className="price-header">
                <h3 className="price-title">{t('pricing.packageTitle')}</h3>
              </div>
              <div className="price-divider"></div>
              <h4 className="included-title">{t('pricing.includedTitle')}</h4>
              <ul className="included-list">
                {Array.isArray(pricingItems) && pricingItems.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
              <Button onClick={scrollToContact} size="lg" className="pricing-cta">{t('pricing.cta')}</Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* About Us */}
      <section className="section" id="about">
        <div className="container">
          <h2 className="section-title">{t('about.title')}</h2>
          <div className="about-content">
            <p className="about-text">{t('about.text1')}</p>
            <p className="about-text">{t('about.text2')}</p>
            <div className="about-stats">
              {Array.isArray(aboutStats) && aboutStats.map((stat, i) => (
                <div className="stat-item" key={i}>
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section section-dark" id="contact">
        <div className="container">
          <h2 className="section-title">{t('contact.title')}</h2>
          <div className="contact-wrapper">
            <div className="contact-info">
              <h3 className="contact-info-title">{t('contact.infoTitle')}</h3>
              <div className="contact-item">
                <Mail className="contact-icon" />
                <div>
                  <div className="contact-label">{t('contact.emailLabel')}</div>
                  <a href="mailto:office@whitecard.hu" className="contact-value">office@whitecard.hu</a>
                </div>
              </div>
              <p className="contact-note">{t('contact.note')}</p>
            </div>
            <Card className="contact-form-card">
              <CardContent className="contact-form-content">
                <form onSubmit={handleSubmit} className="contact-form" noValidate>
                  <input type="text" name="honeypot" value={formData.honeypot} onChange={handleInputChange} style={{ display: 'none' }} tabIndex="-1" autoComplete="off" aria-hidden="true" />

                  <div className="form-group">
                    <Label htmlFor="fullName">{t('contact.form.fullName')}</Label>
                    <Input id="fullName" name="fullName" data-testid="fullName-input" value={formData.fullName} onChange={handleInputChange} placeholder={t('contact.form.fullNamePlaceholder')} disabled={isSubmitting} />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="email">{t('contact.form.email')}</Label>
                    <Input id="email" name="email" type="email" data-testid="email-input" value={formData.email} onChange={handleInputChange} placeholder={t('contact.form.emailPlaceholder')} disabled={isSubmitting} />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="nationality">{t('contact.form.nationality')}</Label>
                    <Input id="nationality" name="nationality" data-testid="nationality-input" value={formData.nationality} onChange={handleInputChange} placeholder={t('contact.form.nationalityPlaceholder')} disabled={isSubmitting} />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="monthlyIncome">{t('contact.form.monthlyIncome')}</Label>
                    <Input id="monthlyIncome" name="monthlyIncome" type="number" data-testid="monthlyIncome-input" value={formData.monthlyIncome} onChange={handleInputChange} placeholder={t('contact.form.monthlyIncomePlaceholder')} min="0" disabled={isSubmitting} />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="employmentType">{t('contact.form.employmentType')}</Label>
                    <Select onValueChange={handleSelectChange} value={formData.employmentType} disabled={isSubmitting}>
                      <SelectTrigger data-testid="employmentType-select">
                        <SelectValue placeholder={t('contact.form.employmentTypePlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">{t('contact.form.employeeOption')}</SelectItem>
                        <SelectItem value="business_owner">{t('contact.form.businessOption')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="form-group checkbox-group">
                    <div className="checkbox-wrapper">
                      <input type="checkbox" id="gdprConsent" name="gdprConsent" data-testid="gdpr-checkbox" checked={formData.gdprConsent} onChange={handleCheckboxChange} disabled={isSubmitting} className="gdpr-checkbox" />
                      <Label htmlFor="gdprConsent" className="gdpr-label">
                        {t('contact.form.gdprLabel')}{' '}
                        <a href="#privacy-policy" className="privacy-link">{t('contact.form.gdprLink')}</a>{' '}
                        {t('contact.form.gdprSuffix')}
                      </Label>
                    </div>
                  </div>
                  <Button type="submit" size="lg" className="submit-btn" data-testid="submit-btn" disabled={isSubmitting}>
                    {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <section id="privacy-policy" className="privacy-section">
            <h3 className="privacy-title">{t('privacy.title')}</h3>
            <div className="privacy-content">
              <p>{t('privacy.intro')}</p>
              <ul className="privacy-list">
                {Array.isArray(privacyItems) && privacyItems.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
              <p>
                <strong>{t('privacy.rightsTitle')}</strong> {t('privacy.rightsText')}
              </p>
              <p>
                {t('privacy.contactText')}{' '}
                <a href="mailto:office@whitecard.hu" className="privacy-email">office@whitecard.hu</a>.
              </p>
            </div>
          </section>

          <div className="footer-divider"></div>

          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">{t('footer.logo')}</div>
              <p className="footer-text">{t('footer.tagline')}</p>
            </div>
            <div className="footer-section">
              <h4 className="footer-title">{t('footer.quickLinks')}</h4>
              <a href="#about" className="footer-link">{t('footer.aboutLink')}</a>
              <a href="#benefits" className="footer-link">{t('footer.benefitsLink')}</a>
              <a href="#eligibility" className="footer-link">{t('footer.eligibilityLink')}</a>
              <a href="#pricing" className="footer-link">{t('footer.pricingLink')}</a>
            </div>
            <div className="footer-section">
              <h4 className="footer-title">{t('footer.contactTitle')}</h4>
              <a href="mailto:office@whitecard.hu" className="footer-link">office@whitecard.hu</a>
              <div className="footer-social">
                <a href="https://www.instagram.com/agm_consulting_bp/" target="_blank" rel="noopener noreferrer" className="footer-social-link">Instagram</a>
                <a href="https://x.com/AGM_BP" target="_blank" rel="noopener noreferrer" className="footer-social-link">X (Twitter)</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
